import React from 'react';
import { Icons } from './Icons';

// Who can see an attachment. `null` is everyone in the tree, which is what
// every attachment did before private files existed; a list is restricted to
// those member uids, and the empty list is "only me". The server always adds
// the uploader and always lets the owner see everything (api/authz.py), so
// neither is offered as a choice here.

// Short enough that all three fit one segmented bar on a phone-width panel.
const MODES = [
  { value: 'everyone', label: 'Everyone' },
  { value: 'me',       label: 'Only me' },
  { value: 'some',     label: 'Choose people' },
];

function modeOf(value) {
  if (value == null) return 'everyone';
  return value.length === 0 ? 'me' : 'some';
}

export function memberName(uid, memberDetails) {
  const d = memberDetails?.[uid];
  return d?.displayName || d?.email || 'A former member';
}

// The members a file can be shared with: everyone on the tree but the owner,
// who always has access, and the person choosing, who is always included.
export function shareableMembers(treeMembers, memberDetails, selfUid) {
  return Object.entries(treeMembers || {})
    .filter(([uid, role]) => uid !== selfUid && role !== 'owner')
    .map(([uid]) => ({ uid, name: memberName(uid, memberDetails) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function VisibilityPicker({ value, onChange, members, isOwner, compact }) {
  const mode = modeOf(value);
  const chosen = new Set(value || []);

  const pick = (next) => {
    if (next === 'everyone') onChange(null);
    else if (next === 'me') onChange([]);
    // Switching to "specific people" keeps whoever was already picked, and
    // starts empty otherwise — an empty pick still saves as "only me", so the
    // file is never wider than the person chose.
    else onChange(value && value.length ? value : []);
  };
  const toggle = (uid) => {
    const next = new Set(chosen);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    onChange([...next]);
  };

  // "Specific people" stays selected while nobody is ticked yet, rather than
  // collapsing back to "Only me" the moment the last box is cleared.
  const [someOpen, setSomeOpen] = React.useState(mode === 'some');
  const shown = someOpen && mode !== 'everyone' ? 'some' : mode;

  return (
    <div style={{ fontSize: compact ? 12 : 13, color: '#2C2418' }}>
      <div role="radiogroup" aria-label="Who can see this" style={{
        display: 'flex', border: '1.5px solid #D5CAB8', borderRadius: 10,
        overflow: 'hidden', background: '#fff',
      }}>
        {MODES.map((m, i) => {
          const selected = shown === m.value;
          return (
            <button key={m.value} type="button" role="radio" aria-checked={selected}
              onClick={() => { setSomeOpen(m.value === 'some'); pick(m.value); }}
              style={{
                flex: 1, minWidth: 0, padding: '8px 4px', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
                border: 'none', borderLeft: i === 0 ? 'none' : '1.5px solid #D5CAB8',
                background: selected ? 'rgba(196,151,90,0.16)' : 'transparent',
                color: selected ? '#8A6530' : '#6B5E4F',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                whiteSpace: 'nowrap',
              }}>
              {m.value === 'everyone' ? <Icons.Users size={12} /> : <Icons.Lock size={11} />}
              {m.label}
            </button>
          );
        })}
      </div>

      {shown === 'some' && (
        <div style={{
          marginTop: 8, padding: '8px 10px', background: '#fff',
          border: '1px solid #EBE4D8', borderRadius: 8, maxHeight: 180, overflowY: 'auto',
        }}>
          {members.length === 0 ? (
            <p style={{ margin: 0, fontSize: 12, color: '#A0937F' }}>
              Nobody else has joined this tree yet. Invite people from Manage Access first.
            </p>
          ) : members.map(m => (
            <label key={m.uid} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer',
            }}>
              <input type="checkbox" checked={chosen.has(m.uid)} onChange={() => toggle(m.uid)} />
              <span style={{ fontSize: 13 }}>{m.name}</span>
            </label>
          ))}
        </div>
      )}

      <p style={{ margin: '6px 0 0', fontSize: 11, color: '#A0937F' }}>
        {mode === 'everyone'
          // "Future" is the difference from picking everyone by hand, and
          // the part worth knowing before uploading.
          ? 'File will be visible to all current and future accounts on this tree.'
          : isOwner
            ? 'Nobody else in the tree will see this file, or know it exists.'
            : "The tree's owner always has access. Nobody else will see this file, or know it exists."}
      </p>
    </div>
  );
}

// The short label on a private attachment's row, with the full list in a
// tooltip. Public attachments get no label: that is the ordinary case.
export function describeVisibility(a, selfUid, memberDetails) {
  if (!a.restricted) return null;
  const others = (a.visibleTo || []).filter(uid => uid !== a.uploadedBy);
  const uploader = a.uploadedBy === selfUid ? 'you' : memberName(a.uploadedBy, memberDetails);
  const names = [uploader, ...others.map(uid => uid === selfUid ? 'you' : memberName(uid, memberDetails))];
  const title = `Visible to ${names.join(', ')} and the tree's owner`;
  if (others.length === 0) {
    return { label: a.uploadedBy === selfUid ? 'Only you' : `Only ${uploader}`, title };
  }
  const label = `${uploader === 'you' ? 'You' : uploader} + ${others.length} other${others.length === 1 ? '' : 's'}`;
  return { label, title };
}
